import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {FocusKeys} from '@primer/behaviors'
import {Box, IconButton, useFocusZone} from '@primer/react'
import {memo, useCallback, useId, useRef, useState} from 'react'

import {ViewOptionsToolbarUI} from '../../api/stats/contracts'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {ViewOptionsStatsUiContext} from '../../hooks/use-view-options-stats-ui-key'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import {type ConfigurationOptionComponentProps, optionsForViewType} from './configuration'

const iconButtonSx = {
  position: 'relative',
  '& .dirty-icon-container': {
    lineHeight: 0,
  },
}

const ActionMenuWithTooltip = ({
  MenuComponent,
  title,
  icon,
  testId,
  hideIf,
  useHasValue,
}: React.PropsWithChildren<ConfigurationOptionComponentProps>) => {
  const {currentView} = useViews()
  const enabledFeatures = useEnabledFeatures()
  const hasValue = useHasValue()
  const contentRef = useRef<HTMLButtonElement>(null)
  const [contentProps, tooltipElement] = usePortalTooltip({
    contentRef,
    'aria-label': title,
  })
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const toggleOpen = useCallback(() => setOpen(s => !s), [])
  if (!currentView || hideIf({currentView, enabledFeatures})) return null
  return (
    <>
      {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
      <IconButton
        unsafeDisableTooltip
        ref={contentRef}
        size="small"
        variant={hasValue ? 'default' : 'invisible'}
        icon={icon}
        sx={iconButtonSx}
        aria-pressed={hasValue ? 'true' : 'false'}
        aria-label={title}
        onClick={toggleOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-expanded={open}
        {...contentProps}
        {...testIdProps(`quick-action-toolbar-${testId}`)}
      />
      <MenuComponent id={menuId} open={open} anchorRef={contentRef} setOpen={setOpen} />
      {tooltipElement}
    </>
  )
}

const toolbarButtonGroupSx = {position: 'relative', display: 'flex', gap: 1}
export const ViewOptionsToolbar = memo(function ViewOptionsToolbar() {
  const ref = useRef<HTMLDivElement>(null)
  useFocusZone({
    containerRef: ref,
    bindKeys: FocusKeys.ArrowHorizontal,
  })

  const {currentView} = useViews()
  const {viewType} = useViewType()
  const enabledFeatures = useEnabledFeatures()

  if (!currentView) return null

  const options = optionsForViewType[viewType]
  if (!options) return null
  return (
    <ViewOptionsStatsUiContext.Provider value={ViewOptionsToolbarUI}>
      <Box role="toolbar" sx={toolbarButtonGroupSx} ref={ref} {...testIdProps('quick-action-toolbar')}>
        {options.map(({ToolbarItemComponent = ActionMenuWithTooltip, ...option}) => {
          if (option.hideIf({currentView, enabledFeatures})) return null

          return <ToolbarItemComponent {...option} key={option.title} />
        })}
      </Box>
    </ViewOptionsStatsUiContext.Provider>
  )
})

try{ ActionMenuWithTooltip.displayName ||= 'ActionMenuWithTooltip' } catch {}
try{ ViewOptionsToolbar.displayName ||= 'ViewOptionsToolbar' } catch {}